import { useCallback, useMemo, useReducer, useRef, useState } from 'react';

import payload from '@app/payload';
import { getLocaleInfo, resolveLocaleForCountry, setLocale } from '@i18n';
import {
  createInitialCheckoutValues,
  ensurePlanInstallments,
  getInitialBuyerCountry,
  resolveCheckoutPricing,
} from '../lib/checkoutBusiness';

import type { CheckoutValues } from '../types';

export function useCheckoutPage() {
  const [refreshKey, refresh] = useReducer((value: number) => value + 1, 0);
  const [selectedCountry, setSelectedCountry] = useState(getInitialBuyerCountry);
  const [locale, setCheckoutLocale] = useState(() => {
    const nextLocale = getLocaleInfo(selectedCountry, resolveLocaleForCountry(selectedCountry));
    setLocale(nextLocale.code);
    return nextLocale;
  });
  const valuesRef = useRef<CheckoutValues>(createInitialCheckoutValues());
  const chargeIdRef = useRef<any>(null);
  const [pixInfo, setPixInfo] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const plan = payload.plans.find((item: any) => String(item.id) === String(valuesRef.current.plan));

  if (!plan) {
    throw new Error('The selected checkout plan was not found.');
  }

  ensurePlanInstallments(valuesRef.current, plan);

  const pricing = useMemo(
    () => resolveCheckoutPricing(plan, selectedCountry),
    [plan, selectedCountry],
  );
  const planForDisplay = useMemo(
    () => ({ ...plan, currency: pricing.currency }),
    [plan, pricing.currency],
  );
  const methodsEnabled = !pricing.isStripe && plan.methods
    ? Object.keys(plan.methods).filter((method) => plan.methods[method].enabled)
    : [];

  const onCountryChange = useCallback((country: string) => {
    const normalizedCountry = String(country || '').toUpperCase();
    const nextLocale = getLocaleInfo(normalizedCountry, resolveLocaleForCountry(normalizedCountry));

    setLocale(nextLocale.code);
    setSelectedCountry(normalizedCountry);
    setCheckoutLocale(nextLocale);
    valuesRef.current.country = normalizedCountry;
    setPixInfo(null);
    refresh();
  }, []);

  const onPlanSelect = useCallback((nextPlan: any) => {
    if (!nextPlan || String(nextPlan.id) === String(valuesRef.current.plan)) {
      return;
    }

    valuesRef.current.plan = nextPlan.id;
    valuesRef.current.installments = nextPlan.installments > 1 ? nextPlan.installments : 1;
    setPixInfo(null);
    refresh();
  }, []);

  return {
    valuesRef,
    chargeIdRef,
    refresh,
    refreshKey,
    selectedCountry,
    locale,
    pixInfo,
    setPixInfo,
    snackbar,
    setSnackbar,
    plan,
    planForDisplay,
    methodsEnabled,
    onCountryChange,
    onPlanSelect,
    ...pricing,
  };
}

export type CheckoutPageState = ReturnType<typeof useCheckoutPage>;
